import { useEffect } from 'react'
import { useAppDispatch, useUrlSearchParams } from '@/hooks'
import { selectUtility } from '@/actions/utilities'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import { useMultiProgramRouteContext } from '..'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import { ProfileEligibilityRequirements } from '@/types/programs-new/profile-requirements'
import { Utility } from '@/types'
import { UtilityPlanSelection } from '@/app/features/utility-plan-selection'

/**
 * Enrollment wrapper around the shared utility/plan selection page. Supplies the
 * program-derived behaviour (eligibility gating, external-connection requirement,
 * next-step routing) from `useProgramRequirements`.
 */
export default function UtilitySelection() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const flow = useMultiProgramFlowControl()
  const dispatch = useAppDispatch()
  const {
    routeToNextStep,
    requirementData,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
    programRequirements,
  } = useProgramRequirements(utilityProgram)

  const planRequired = requirementData
    .map((req) => req.key)
    .includes(ProfileEligibilityRequirements.RequirePlan)

  const onExternalUtilityConnectButtonClick = async (utility: Utility) => {
    flow.setFlowStartPoint(location.pathname, utilityProgram.id)
    await dispatch(selectUtility({ utility_id: utility.id }))
  }

  const searchParams = useUrlSearchParams()
  useEffect(() => {
    const utilityConnectSuccess =
      searchParams.get('utility-connect-success') === 'true'
    if (utilityConnectSuccess && programRequirements) {
      routeToNextStep()
    }
  }, [programRequirements])

  return (
    <UtilityPlanSelection
      utilityProgram={utilityProgram}
      planRequired={planRequired}
      onContinue={routeToNextStep}
      hasExternalUtilityConnectionRequirement={
        hasExternalUtilityConnectionRequirement
      }
      hasCompletedExternalUtilityConnectionRequirement={
        hasCompletedExternalUtilityConnectionRequirement
      }
      onExternalUtilityConnectButtonClick={onExternalUtilityConnectButtonClick}
    />
  )
}
